import React from 'react';
import { BsX, BsPlayCircle, BsCloudUpload } from 'react-icons/bs';

interface HelpDrawerVideosProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string; textColor?: string }> = ({ icon, label, description, bgColor, borderColor, textColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
      color: textColor || 'black',
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerVideos: React.FC<HelpDrawerVideosProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Vídeos</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: 0 }}>
              Gerencie os vídeos dos candidatos vinculados à banca. Faça upload de vídeos para bancas virtuais e visualize os já enviados.
            </p>
          </Section>

          <Section title="Estatísticas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Total:</strong> Quantidade de candidatos na banca.</li>
              <li><strong>Faltam Upload:</strong> Candidatos elegíveis sem vídeo enviado.</li>
              <li><strong>Com Vídeo:</strong> Candidatos que já possuem vídeo.</li>
            </ul>
          </Section>

          <Section title="Colunas da Tabela">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Nome:</strong> Nome do candidato.</li>
              <li><strong>Nome Social:</strong> Nome social (se houver).</li>
              <li><strong>Inscrição:</strong> Número de inscrição no edital.</li>
              <li><strong>Status:</strong> Status do candidato na banca (ícone colorido).</li>
              <li><strong>Resultado:</strong> Resultado da votação (se já houver).</li>
              <li><strong>Ações:</strong> Visualizar e enviar vídeo.</li>
            </ul>
          </Section>

          <Section title="Ações por Candidato">
            <ActionItem icon={<BsPlayCircle />} label="Visualizar Vídeo" description="Abre o vídeo do candidato para visualização. Desabilitado se não houver vídeo enviado." bgColor="#212529" borderColor="#212529" textColor="white" />
            <ActionItem icon={<BsCloudUpload />} label="Upload de Vídeo" description="Abre o modal para enviar o vídeo do candidato. Selecione o arquivo e confirme o envio." bgColor="#0d6efd" borderColor="#0d6efd" textColor="white" />
          </Section>

          <Section title="Upload de Vídeo">
            <ol style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Clique no botão de upload (azul) do candidato</li>
              <li>Selecione o arquivo de vídeo</li>
              <li>Opcionalmente ajuste o nome do arquivo</li>
              <li>Clique em <strong>Enviar</strong></li>
              <li>Aguarde o upload completar</li>
            </ol>
          </Section>

          <Section title="Indicadores de Status">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: '#28a745' }}>✓</span>
                <span><strong>Validado:</strong> Candidato aprovado.</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: '#dc3545' }}>✗</span>
                <span><strong>Não Validado:</strong> Candidato reprovado.</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: '#6c757d' }}>—</span>
                <span><strong>Ausente:</strong> Não compareceu.</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: '#ffc107' }}>!</span>
                <span><strong>Inconclusivo:</strong> Resultado indefinido.</span>
              </div>
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Envie todos os vídeos antes do início da sessão da banca.</li>
              <li>Linhas coloridas indicam o status: verde (validado), vermelho (não validado), cinza (ausente).</li>
              <li>O botão de visualizar fica desabilitado (vermelho) se o candidato não tem vídeo.</li>
              <li>Use os filtros e paginação para navegar em bancas com muitos candidatos.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerVideos;
